/*
  Camille & Co. cart drawer (sections/camille-cart-drawer.liquid).
  Every cart change asks Shopify for the freshly rendered section and swaps in its
  [data-cc-cart-inner] part, so prices, discounts and the tier bar always come from
  the real cart. Header hooks kept from the theme: [data-cart-toggle], [data-cart-count].

  Kaching Cart app: while its cart is switched on (app embed in Theme settings > App embeds),
  Kaching opens its own drawer after every add to cart and on the cart links, so this drawer
  stays closed: it still adds products from the product form and updates the cart count.
  Switch the Kaching embed off and this drawer opens again as before.
*/
(function () {
  'use strict';

  var ROOT = '[data-cc-cart]';
  var FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* True while the Kaching Cart app embed is on (its settings object is printed inline in the page) */
  function kachingCartActive() {
    return !!((window.kachingCartApi || window.__kachingCartProps) && !window.__kachingCartDisabled);
  }

  function CamilleCart() {
    this.isOpen = false;
    this.busy = false;
    this.lastActive = null;
    this.inerted = [];
    if (!this.root()) return;

    document.addEventListener('click', this.onClick.bind(this));
    document.addEventListener('change', this.onChange.bind(this));
    document.addEventListener('submit', this.onSubmit.bind(this));
    document.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('shopify:section:select', this.onEditorSelect.bind(this));
    document.addEventListener('shopify:section:deselect', this.onEditorDeselect.bind(this));
    document.addEventListener('shopify:section:load', this.onEditorLoad.bind(this));

    document.querySelectorAll('[data-cart-toggle]').forEach(function (toggle) {
      toggle.setAttribute('aria-haspopup', 'dialog');
      toggle.setAttribute('aria-expanded', 'false');
    });
  }

  CamilleCart.prototype.root = function () {
    return document.querySelector(ROOT);
  };

  CamilleCart.prototype.sectionId = function () {
    var root = this.root();
    return root ? root.dataset.sectionId : '';
  };

  /* ---------- events ---------- */

  CamilleCart.prototype.onClick = function (event) {
    var toggle = event.target.closest('[data-cart-toggle]');
    if (toggle) {
      if (kachingCartActive()) return; // Kaching opens its own cart from this link
      event.preventDefault();
      this.lastActive = toggle;
      this.open();
      this.refresh();
      return;
    }

    var root = this.root();
    if (!root || !root.contains(event.target)) return;

    if (event.target.closest('[data-cc-cart-close]')) {
      this.close();
      return;
    }

    var qtyButton = event.target.closest('[data-cc-qty]');
    if (qtyButton) {
      var line = qtyButton.closest('[data-cc-line-key]');
      if (!line) return;
      this.change(line.dataset.ccLineKey, Math.max(0, Number(qtyButton.dataset.ccQty) || 0));
    }
  };

  CamilleCart.prototype.onChange = function (event) {
    var input = event.target.closest('[data-cc-qty-input]');
    if (!input) return;
    var line = input.closest('[data-cc-line-key]');
    if (!line) return;
    this.change(line.dataset.ccLineKey, Math.max(0, parseInt(input.value, 10) || 0));
  };

  CamilleCart.prototype.onSubmit = function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (form.matches('[data-cc-upsell-form]')) {
      event.preventDefault();
      var idField = form.querySelector('[name="id"]');
      if (idField && idField.value) this.addUpsell(Number(idField.value));
      return;
    }

    if (form.matches('[data-product-form]')) {
      var submitter = event.submitter;
      if (!submitter || submitter.getAttribute('name') !== 'add') return;
      event.preventDefault();
      this.addFromProductForm(form, submitter);
    }
  };

  CamilleCart.prototype.onKeydown = function (event) {
    if (!this.isOpen) return;
    if (event.key === 'Escape') {
      this.close();
    } else if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  };

  /* Theme editor: open the drawer while its settings are selected */
  CamilleCart.prototype.onEditorSelect = function (event) {
    if (event.detail && event.detail.sectionId === this.sectionId()) this.open();
  };

  CamilleCart.prototype.onEditorDeselect = function (event) {
    if (event.detail && event.detail.sectionId === this.sectionId()) this.close();
  };

  CamilleCart.prototype.onEditorLoad = function (event) {
    if (event.detail && event.detail.sectionId === this.sectionId() && this.isOpen) {
      this.isOpen = false;
      this.open();
    }
  };

  /* ---------- cart requests ---------- */

  CamilleCart.prototype.postJSON = function (path, body) {
    return fetch(routeRoot() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().catch(function () { return null; }).then(function (data) {
        if (!response.ok) {
          throw new Error((data && (data.description || data.message)) || 'Cart request failed');
        }
        return data;
      });
    });
  };

  CamilleCart.prototype.change = function (key, quantity) {
    if (!key || this.busy) return;
    var self = this;
    this.setBusy(true, key);

    this.postJSON('cart/change.js', {
      id: key,
      quantity: quantity,
      sections: [this.sectionId()],
      sections_url: window.location.pathname
    })
      .then(function (data) { self.renderSections(data && data.sections); })
      .catch(function (error) {
        console.error(error);
        self.announce(error.message);
        return self.refresh();
      })
      .then(function () { self.setBusy(false); });
  };

  CamilleCart.prototype.addUpsell = function (variantId) {
    if (!variantId || this.busy) return;
    var self = this;
    this.setBusy(true);

    this.postJSON('cart/add.js', {
      items: [{ id: variantId, quantity: 1 }],
      sections: [this.sectionId()],
      sections_url: window.location.pathname
    })
      .then(function (data) { self.renderSections(data && data.sections); })
      .catch(function (error) {
        console.error(error);
        self.announce(error.message);
        return self.refresh();
      })
      .then(function () { self.setBusy(false); });
  };

  CamilleCart.prototype.addFromProductForm = function (form, button) {
    var self = this;
    var label = button.querySelector('[data-add-to-cart-label]');
    var labelText = label ? label.textContent : '';
    var root = this.root();
    var errorNode = form.querySelector('[data-add-to-cart-error]');

    if (errorNode) {
      errorNode.hidden = true;
      errorNode.textContent = '';
    }

    button.disabled = true;
    if (label && root && root.dataset.addingLabel) label.textContent = root.dataset.addingLabel;

    var formData = new FormData(form);
    formData.append('sections', this.sectionId());
    formData.append('sections_url', window.location.pathname);

    fetch(routeRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    })
      .then(function (response) {
        return response.json().catch(function () { return null; }).then(function (data) {
          if (!response.ok) {
            throw new Error((data && (data.description || data.message)) || '');
          }
          return data;
        });
      })
      .then(function (data) {
        self.lastActive = button;
        self.renderSections(data && data.sections);
        if (!kachingCartActive()) self.open(); // Kaching opens its own cart after this request
      })
      .catch(function (error) {
        console.error(error);
        if (!errorNode) {
          window.location.href = form.action || routeRoot() + 'cart';
          return;
        }
        errorNode.textContent = error.message || errorNode.dataset.genericError || '';
        errorNode.hidden = false;
      })
      .then(function () {
        button.disabled = false;
        if (label) label.textContent = labelText;
      });
  };

  CamilleCart.prototype.refresh = function () {
    var self = this;
    var id = this.sectionId();
    if (!id) return Promise.resolve();

    return fetch(routeRoot() + '?sections=' + encodeURIComponent(id), { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (sections) { self.renderSections(sections, true); })
      .catch(function (error) { console.error(error); });
  };

  /* ---------- rendering ---------- */

  CamilleCart.prototype.renderSections = function (sections, quiet) {
    var html = sections && sections[this.sectionId()];
    if (!html) {
      if (!quiet) this.refresh();
      return;
    }

    var root = this.root();
    var current = root && root.querySelector('[data-cc-cart-inner]');
    var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cc-cart-inner]');
    if (!current || !fresh) return;

    var focus = this.captureFocus(current);
    var oldBody = current.querySelector('[data-cc-cart-body]');
    var scrollTop = oldBody ? oldBody.scrollTop : 0;

    current.replaceWith(fresh);

    var newBody = fresh.querySelector('[data-cc-cart-body]');
    if (newBody) newBody.scrollTop = scrollTop;
    this.restoreFocus(fresh, focus);

    var count = Number(fresh.dataset.itemCount || 0);
    document.querySelectorAll('[data-cart-count]').forEach(function (node) {
      node.textContent = count;
      node.hidden = count === 0;
    });

    if (!quiet) this.announce(fresh.dataset.liveText || '');
  };

  CamilleCart.prototype.captureFocus = function (container) {
    var active = document.activeElement;
    if (!active || !container.contains(active) || !active.dataset.ccFocus) return null;
    var line = active.closest('[data-cc-line-key]');
    return { role: active.dataset.ccFocus, key: line ? line.dataset.ccLineKey : null };
  };

  CamilleCart.prototype.restoreFocus = function (container, focus) {
    if (!focus || !this.isOpen) return;
    var target = null;

    if (focus.key) {
      container.querySelectorAll('[data-cc-line-key]').forEach(function (line) {
        if (line.dataset.ccLineKey === focus.key) {
          target = line.querySelector('[data-cc-focus="' + focus.role + '"]');
        }
      });
    } else {
      target = container.querySelector('[data-cc-focus="' + focus.role + '"]');
    }

    (target || container.querySelector('#CcCartTitle')).focus();
  };

  CamilleCart.prototype.announce = function (text) {
    var live = this.root() && this.root().querySelector('[data-cc-cart-live]');
    if (live) live.textContent = text || '';
  };

  CamilleCart.prototype.setBusy = function (isBusy, key) {
    this.busy = isBusy;
    var root = this.root();
    if (!root) return;
    root.classList.toggle('is-busy', isBusy);
    if (!key) return;
    root.querySelectorAll('[data-cc-line-key]').forEach(function (line) {
      if (line.dataset.ccLineKey === key) {
        line.classList.toggle('is-pending', isBusy);
        line.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      }
    });
  };

  /* ---------- open / close ---------- */

  CamilleCart.prototype.open = function () {
    var root = this.root();
    if (!root || this.isOpen) return;
    this.isOpen = true;

    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cc-cart-open');
    document.querySelectorAll('[data-cart-toggle]').forEach(function (toggle) {
      toggle.setAttribute('aria-expanded', 'true');
    });

    this.inerted = Array.prototype.filter.call(document.body.children, function (el) {
      return !el.contains(root) && el.tagName !== 'SCRIPT' && !el.hasAttribute('inert');
    });
    this.inerted.forEach(function (el) { el.setAttribute('inert', ''); });

    var title = root.querySelector('#CcCartTitle');
    if (title) title.focus({ preventScroll: true });
  };

  CamilleCart.prototype.close = function () {
    var root = this.root();
    if (!root || !this.isOpen) return;
    this.isOpen = false;

    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cc-cart-open');
    document.querySelectorAll('[data-cart-toggle]').forEach(function (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
    });

    this.inerted.forEach(function (el) { el.removeAttribute('inert'); });
    this.inerted = [];

    if (this.lastActive && document.contains(this.lastActive)) this.lastActive.focus();
  };

  CamilleCart.prototype.trapFocus = function (event) {
    var root = this.root();
    var panel = root && root.querySelector('.cc-cart__panel');
    if (!panel) return;

    var nodes = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (node) {
      return !node.disabled && node.offsetParent !== null;
    });
    if (!nodes.length) return;

    var first = nodes[0];
    var last = nodes[nodes.length - 1];

    if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  function start() {
    window.camilleCart = new CamilleCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
